import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Zap, ArrowLeft, Trash2, Download, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DataRetention = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExportData = async () => {
    if (!user) {
      toast.error('Please log in to export your data');
      navigate('/login');
      return;
    }

    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/auth/export-data`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nexus-leads-data-${Date.now()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Your data has been exported successfully');
    } catch (error) {
      toast.error('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/auth/delete-account`, {
        withCredentials: true
      });
      
      toast.success('Your account has been deleted');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-indigo-700 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-slate-900">Nexus Leads</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-heading font-bold text-slate-900 mb-2">Data Retention & Deletion</h1>
        <p className="text-slate-500 mb-8">Last updated: January 2026</p>

        <div className="bg-white rounded-2xl border border-slate-200 p-8 space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Data Retention Periods</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We retain different types of data for different periods based on legal requirements 
              and business needs:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-medium text-slate-800">Data Type</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-800">Retention Period</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-800">Reason</th>
                  </tr>
                </thead>
                <tbody className="text-slate-600">
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4">Account Information</td>
                    <td className="py-3 px-4">Until account deletion</td>
                    <td className="py-3 px-4">Service operation</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4">Saved Leads & CRM Data</td>
                    <td className="py-3 px-4">Until account deletion</td>
                    <td className="py-3 px-4">User-generated content</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4">Payment Records</td>
                    <td className="py-3 px-4">7 years after transaction</td>
                    <td className="py-3 px-4">Legal/tax requirements</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4">Usage Analytics</td>
                    <td className="py-3 px-4">24 months</td>
                    <td className="py-3 px-4">Service improvement</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4">Security Logs</td>
                    <td className="py-3 px-4">12 months</td>
                    <td className="py-3 px-4">Security & fraud prevention</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4">Support Communications</td>
                    <td className="py-3 px-4">3 years</td>
                    <td className="py-3 px-4">Customer service records</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Your Data Rights</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Under various privacy regulations (GDPR, CCPA, etc.), you have the right to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of all personal data we hold about you</li>
              <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
              <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong>Restriction:</strong> Limit how we process your data</li>
              <li><strong>Objection:</strong> Object to certain types of processing</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">3. Export Your Data</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              You can download a copy of all your personal data, including your profile information, 
              saved leads, contact history, and account settings.
            </p>
            {user ? (
              <Button 
                onClick={handleExportData}
                disabled={exporting}
                className="btn-primary"
                data-testid="export-data-btn"
              >
                {exporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export My Data
                  </>
                )}
              </Button>
            ) : (
              <p className="text-slate-500 italic">
                <Link to="/login" className="text-indigo-600 hover:underline">Log in</Link> to export your data.
              </p>
            )}
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Delete Your Account</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              You can permanently delete your account and all associated data. This action is 
              irreversible and will:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4 mb-4">
              <li>Delete your profile and account information</li>
              <li>Remove all saved leads and CRM data</li>
              <li>Cancel any active subscriptions</li>
              <li>Revoke access to the platform</li>
            </ul>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <p className="text-amber-800 text-sm">
                <strong>Note:</strong> Some data may be retained for legal compliance (e.g., payment 
                records for tax purposes). This retained data will be anonymized where possible.
              </p>
            </div>
            
            {user ? (
              !showDeleteConfirm ? (
                <Button 
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="delete-account-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete My Account
                </Button>
              ) : (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-red-800">Are you sure?</h4>
                      <p className="text-sm text-red-700 mt-1">
                        This action cannot be undone. All your data will be permanently deleted.
                      </p>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-red-800 mb-2">
                      Type DELETE to confirm:
                    </label>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      className="w-full px-3 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                      placeholder="DELETE"
                      data-testid="delete-confirm-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleDeleteAccount}
                      disabled={deleting || deleteConfirmText !== 'DELETE'}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="confirm-delete-btn"
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        'Permanently Delete Account'
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )
            ) : (
              <p className="text-slate-500 italic">
                <Link to="/login" className="text-indigo-600 hover:underline">Log in</Link> to delete your account.
              </p>
            )}
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed">
              For data-related requests or questions, contact our Data Protection team at{' '}
              <a href="mailto:support@nexusleads.us" className="text-indigo-600 hover:underline">
                support@nexusleads.us
              </a>
              . We will respond to your request within 30 days.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 bg-slate-900 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-400 text-sm">
            © 2026 Nexus Leads. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DataRetention;
